import { AnimatePresence, motion } from "framer-motion";
import React, { memo, useEffect, useMemo, useRef } from "react";
import {
  ExtendedNotificationType,
  NotificationItem,
  useFilteredNotifications,
  useNotificationStore,
} from "@/stores/notifications/notificationStore";
import { MAX_VISIBLE_SIZE } from "@/domains/notifications/constants/notificationsGlobal";
import { notificationsComponents } from "@/domains/notifications/helpers/notificationsComponentsList";
import useAddOfferSubscriber from "@/domains/notifications/hooks/useAddOfferSubscriber.ts";
import useKeyboardEvent from "@/domains/notifications/hooks/useKeyboardEvent.ts";

const NOTIFICATION_TYPES = [
  ExtendedNotificationType.Action,
  ExtendedNotificationType.Transaction,
  ExtendedNotificationType.Offer,
];

function NotificationsContainer() {
  useAddOfferSubscriber();
  useKeyboardEvent();

  const timerRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const { removeNotification } = useNotificationStore();
  const notificationsStack = useFilteredNotifications(NOTIFICATION_TYPES);

  const visibleNotifications = useMemo(() => {
    const visible: NotificationItem[] = [];
    let currentSize = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const item of notificationsStack) {
      const itemSize = notificationsComponents[item.type].size;
      if (currentSize + itemSize <= MAX_VISIBLE_SIZE) {
        visible.push(item);
        currentSize += itemSize;
      } else {
        break;
      }
    }

    return visible;
  }, [notificationsStack]);

  useEffect(() => {
    visibleNotifications.forEach((item) => {
      if (timerRef.current.has(item.id)) return;

      const timer = setTimeout(() => {
        timerRef.current.delete(item.id);
        removeNotification(item.id);
      }, 5000);

      timerRef.current.set(item.id, timer);
    });

    const currentIds = new Set(notificationsStack.map((item) => item.id));
    timerRef.current.forEach((timeout, id) => {
      if (!currentIds.has(id)) {
        clearTimeout(timeout);
        timerRef.current.delete(id);
      }
    });
  }, [visibleNotifications, notificationsStack, removeNotification]);

  return (
    <div className="flex flex-col gap-[0.625rem] w-[16.9rem] h-[14.8rem] overflow-hidden">
      <AnimatePresence>
        {visibleNotifications.map((item) => (
          <motion.div
            key={item.id}
            layout
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              x: -100,
              transition: { duration: 0.5, ease: "easeInOut" },
            }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {React.createElement(notificationsComponents[item.type].component, {
              item,
            })}
            {/* <pre>{JSON.stringify(item, null, 2)}</pre> */}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

export default memo(NotificationsContainer);
